/**
 * Phase 20 — SAP S/4HANA Integration Health Widget
 * Displays ERP connectivity, BAPI sync throughput, retry queue length, and transaction faults.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IntegrationHealthMetrics } from '@/types/domain/procurement-dashboard';
import { Database, CheckCircle2, AlertTriangle, RefreshCw, ArrowRight } from 'lucide-react';
import { ROUTES } from '@/constants/routes';

interface IntegrationHealthWidgetProps {
  metrics: IntegrationHealthMetrics;
}

export const IntegrationHealthWidget: React.FC<IntegrationHealthWidgetProps> = ({ metrics }) => {
  const navigate = useNavigate();

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-2xs font-heading space-y-4">
      <div className="flex items-center justify-between border-b border-border/60 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400">
            <Database className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-base font-extrabold text-foreground tracking-tight">
              SAP S/4HANA Integration Health
            </h2>
            <p className="text-xs text-foreground-secondary font-medium">
              Bi-directional RFC BAPI synchronization ledger and enterprise connector state
            </p>
          </div>
        </div>

        <button
          onClick={() => navigate(ROUTES.SAP.PR)}
          className="text-xs font-bold text-primary hover:underline flex items-center gap-1"
        >
          SAP Simulator <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* Connection Status */}
        <div className="p-3.5 rounded-xl border border-border bg-surface space-y-1">
          <span className="text-[10px] font-extrabold uppercase text-foreground-muted block">
            Connector State
          </span>
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-base font-black text-foreground">ONLINE</span>
          </div>
          <span className="text-[10px] text-foreground-secondary block">
            Client 100 • S/4HANA 2023
          </span>
        </div>

        {/* Synced Documents */}
        <div
          onClick={() => navigate(ROUTES.SAP.PR)}
          className="p-3.5 rounded-xl border border-border bg-surface hover:border-primary/50 transition-all cursor-pointer space-y-1"
        >
          <span className="text-[10px] font-extrabold uppercase text-foreground-muted flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" /> Replicated Docs
          </span>
          <span className="text-xl font-black text-foreground">{metrics.syncedCount.toLocaleString()}</span>
          <span className="text-[10px] text-foreground-secondary block">PR, PO, GRN, MIRO, F110</span>
        </div>

        {/* Retry Queue */}
        <div
          onClick={() => navigate(ROUTES.SAP.PR)}
          className="p-3.5 rounded-xl border border-border bg-surface hover:border-primary/50 transition-all cursor-pointer space-y-1"
        >
          <span className="text-[10px] font-extrabold uppercase text-foreground-muted flex items-center gap-1">
            <RefreshCw className="h-3 w-3 text-blue-500" /> Auto-Retry Queue
          </span>
          <span className="text-xl font-black text-foreground">{metrics.retryQueueLength}</span>
          <span className="text-[10px] text-foreground-secondary block">Exponential backoff</span>
        </div>

        {/* Failed Sync Count */}
        <div
          onClick={() => navigate(ROUTES.SAP.PR)}
          className="p-3.5 rounded-xl border border-border bg-surface hover:border-primary/50 transition-all cursor-pointer space-y-1"
        >
          <span className="text-[10px] font-extrabold uppercase text-foreground-muted flex items-center gap-1">
            <AlertTriangle className="h-3 w-3 text-rose-500" /> Failed Syncs
          </span>
          <span className="text-xl font-black text-rose-600 dark:text-rose-400">
            {metrics.failedSyncCount}
          </span>
          <span className="text-[10px] text-foreground-secondary block">Requiring re-trigger</span>
        </div>
      </div>
    </div>
  );
};
